import { useEffect, useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useToasts } from '@/hooks/useToasts'
import { useRouter } from 'next/compat/router'
import { UseFormMethods } from 'react-hook-form'

import { formatISO } from 'date-fns'

import { currentDateRounded } from '@/components/ui'
import {
  useAssociateEmailRecipientsMutation,
  useCreateRuleMutation,
  useDevicesDoorQuery,
  useFacilityTypesQuery,
  useFloorsByFacilityIdsQuery,
  useManualIncidentCategoriesQuery,
  useRuleRecipientsQuery,
  useUpdateAiOperatorResponseRulesMutation,
} from '@/graphql/generated/hooks'
import {
  Action,
  AiOperatorActionType,
  AiOperatorResponseRuleInput,
  CreateRuleInput,
  DeviceType,
  EmailRecipientInput,
  FacilityType,
  RepeatInterval,
  RuleEventType,
} from '@/graphql/generated/schemas'
import {
  OptionI,
  getIncidentFacilityOptions,
} from '@/utils/forms/optionBuilders'
import { INCIDENT_RULES } from '@/utils/routes'

import { Criterion } from '../ai-operator-criteria/types/types'
import { CreatableOptionI, DayOptionI, FormInputsT } from '../types/types'
import {
  DAYS,
  adjustEndTimeForCrossDay,
  formatDateDay,
  getDevicesByFacilityIdsOptions,
  getEventOptions,
  getFacilityTimeZone,
  getFloorByFacilityIdsOptions,
  getManualCategoryIds,
  getOptionValues,
  getRuleEmails,
  getRuleLogicInput,
  getSelectedDaysAsString,
  getTimeInUTC,
  mapRuleEventsInput,
  setValidDevicesForFloorsSelected,
} from '../utils/utils'

export type UseCreateIncidentRulesIProps = {
  form: UseFormMethods<FormInputsT>
  criteria?: Criterion[]
  isActionAiOperator?: boolean
}

export const useCreateIncidentRules = ({
  form,
  criteria,
  isActionAiOperator,
}: UseCreateIncidentRulesIProps) => {
  const { getValues, reset, watch } = form
  const { push } = useRouter()
  const { showSuccess, showError } = useToasts()
  const [shouldIncludeAllFloors, setShouldIncludeAllFloors] = useState(true)
  const [shouldIncludeAllEvents, setShouldIncludeAllEvents] = useState(true)
  const [shouldIncludeAllDevices, setShouldIncludeAllDevices] = useState(true)
  const [shouldRepeat, setShouldRepeat] = useState(false)
  const [shouldShowDays, setShouldShowDays] = useState(false)
  const [shouldEnd, setShouldEnd] = useState(false)
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(null)
  const [selectedFloorIds, setSelectedFloorIds] = useState<string[]>([])
  const [numberOfRepeats, setNumberOfRepeats] = useState(1)
  const [days, setDays] = useState<DayOptionI[]>(DAYS)
  const [selectedStartDate, setSelectedStartDate] =
    useState<Date>(currentDateRounded)
  const [selectedEndTime, setSelectedEndTime] = useState<Date>(null)
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(null)
  const [isAllDay, setIsAllDay] = useState(false)
  const watchAction = watch('action')

  const isAiOperatorEnabled = useFeatureFlag(FeatureFlag.aiOperator)

  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityTypesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          typeNot: FacilityType.Market,
        },
      },
    })

  const { data: floorsData, loading: isFloorsLoading } =
    useFloorsByFacilityIdsQuery({
      fetchPolicy: 'network-only',
      variables: { ids: [selectedFacilityId] },
      skip: !selectedFacilityId,
    })

  const { data: devicesData, loading: isDevicesLoading } = useDevicesDoorQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        facilityId: selectedFacilityId,
        floorIdIn: selectedFloorIds,
        type: DeviceType.Door,
      },
    },
    skip: !selectedFacilityId,
  })

  const { data: ruleRecipientsData, loading: isRecipientsLoading } =
    useRuleRecipientsQuery({
      skip: getValues().action?.value !== Action.SendEmail,
    })
  const { data: categoriesData } = useManualIncidentCategoriesQuery()

  const [createRuleMutation, { loading: isLoading }] = useCreateRuleMutation()
  const [associateEmailRecipients] = useAssociateEmailRecipientsMutation()
  const [updateAiOperatorRuleMutation, { loading: isAiOperatorRulesLoading }] =
    useUpdateAiOperatorResponseRulesMutation()

  const facilityOptions = getIncidentFacilityOptions(facilitiesData)
  const floorOptions = getFloorByFacilityIdsOptions(floorsData)
  const deviceOptions = getDevicesByFacilityIdsOptions(devicesData)
  const timeZone =
    selectedFacilityId && facilitiesData
      ? getFacilityTimeZone(facilitiesData, selectedFacilityId)
      : null

  const emailOptions = useMemo(
    () => getRuleEmails(ruleRecipientsData),
    [ruleRecipientsData]
  )

  const updateSelectedFacilityId = (option: OptionI) => {
    setSelectedFacilityId(option?.value)

    // Reset floor+device options when the facility is changed
    reset({
      ...getValues(),
      floor: null,
      device: null,
    })
  }

  const isActionOptionDisabled = (action: OptionI<Action>) =>
    !isAiOperatorEnabled && action.value === Action.EnableAiOperator

  // Floors
  const updateToggleAllFloors = (
    isToogleAll: boolean,
    selectedFloorOptions: OptionI[]
  ) => {
    setShouldIncludeAllFloors(isToogleAll)
    const options = isToogleAll ? floorOptions : selectedFloorOptions
    updateSelectedFloorIds(options)
  }

  const updateSelectedFloorIds = (options: OptionI[]) => {
    const floorIds = getOptionValues(options)
    setSelectedFloorIds(floorIds)
    if (options?.length <= 0) {
      reset({
        ...getValues(),
        device: null,
      })
    }
  }

  const updateNumberOfRepeats = (
    _valueAsString: string,
    valueAsNumber: number
  ) => {
    setNumberOfRepeats(valueAsNumber)
  }

  const updateSelectedDays = (value: string) => {
    const updatedDays = days.map((d) => {
      if (value === d.value) {
        return { ...d, selected: !d.selected }
      }
      return d
    })
    setDays(updatedDays)
  }

  const updateAiOperatorResponseRules = async (
    ruleId: string
  ): Promise<boolean> => {
    try {
      if (!criteria?.length) {
        throw new Error('No criteria provided for AI Operator rules')
      }

      const responseRules: AiOperatorResponseRuleInput[] = criteria?.map(
        (c) => ({
          action: AiOperatorActionType.ResolveAndMarkAsFalse,
          detected: true,
          fieldId: c?.id,
        })
      )
      await updateAiOperatorRuleMutation({
        variables: {
          input: {
            aiOperatorDefaultAction: AiOperatorActionType.FlagAndRelease,
            aiOperatorResponseRules: responseRules,
            ruleId,
          },
        },
      })
      return true
    } catch (e) {
      showError(e?.message)
      return false
    }
  }

  const updateEndTime = (endTime: Date) => {
    const adjustedEndTime = adjustEndTimeForCrossDay(selectedStartDate, endTime)
    setSelectedEndTime(adjustedEndTime)
  }

  const updateDate = (date: Date) => {
    setSelectedStartDate(date)

    if (selectedEndTime) {
      const adjustedEndTime = adjustEndTimeForCrossDay(date, selectedEndTime)
      setSelectedEndTime(adjustedEndTime)
    }
  }

  const createRule = async (formValues: FormInputsT) => {
    const action = formValues.action.value
    const manualIncidentCategoryIds = getManualCategoryIds(
      formValues?.event,
      categoriesData
    )
    const hasManualCategories = manualIncidentCategoryIds?.length > 0
    const events = shouldIncludeAllEvents
      ? [RuleEventType.All]
      : mapRuleEventsInput(formValues?.event, categoriesData)
    const devices = shouldIncludeAllDevices
      ? getOptionValues(deviceOptions)
      : getOptionValues(formValues?.device)
    const repeatType: RepeatInterval = formValues.repeatType?.value
    const shouldReapeatWeekly =
      shouldRepeat && repeatType === RepeatInterval.Weekly

    const createRuleInput: CreateRuleInput = {
      action,
      name: formValues?.name,
      ruleEventType: events,
      ruleLogics: getRuleLogicInput(devices),
      startTime: getTimeInUTC(selectedStartDate, timeZone),
      allDayOverride: isAllDay,
      // Optional inputs
      ...(hasManualCategories && {
        // Add ids of all Manual events
        manualIncidentCategoryIds: manualIncidentCategoryIds,
      }),
      ...(!isAllDay && {
        endTimeOfDay: formatDateDay(
          selectedEndTime,
          selectedStartDate,
          timeZone
        ),
      }),
      ...(shouldReapeatWeekly && { day: getSelectedDaysAsString(days) }),
      ...(shouldRepeat && { repeatInterval: repeatType }),
      ...(shouldRepeat && { repeatIntervalNum: numberOfRepeats }),
      ...(shouldEnd && {
        endDate: formatISO(selectedEndDate, { representation: 'date' }),
      }),
    }

    try {
      const { data } = await createRuleMutation({
        variables: {
          input: createRuleInput,
        },
      })
      const ruleId = data?.createRule?.rule?.id
      if (action === Action.SendEmail) {
        await associateRecipients(formValues.emails, ruleId)
      }
      // Handle AI Operator rules if needed
      if (ruleId && isActionAiOperator) {
        await updateAiOperatorResponseRules(ruleId)
      }
      showSuccess('Successfully created Rule')
      push(INCIDENT_RULES)
    } catch (e) {
      showError(e?.message)
    }
  }

  const associateRecipients = async (
    options: CreatableOptionI[],
    ruleId: string
  ) => {
    const emailRecipients: EmailRecipientInput[] = options.map((option) => {
      // Get the email from the label
      const email = option.label.split(' ')?.[0]
      return {
        email,
        ...(!option.__isNew__ && { userId: option.value }),
      }
    })
    await associateEmailRecipients({
      variables: { input: { ruleId, emailRecipients } },
    })
  }

  const eventOptions = useMemo(
    () => getEventOptions(watchAction?.value, categoriesData),
    [categoriesData, watchAction?.value]
  )

  useEffect(() => {
    // Validate that device option is on selected floor. Call when devicesData has updated
    if (devicesData) {
      setValidDevicesForFloorsSelected(form, devicesData)
    }
  }, [devicesData])

  return {
    eventOptions,
    shouldIncludeAllFloors,
    shouldIncludeAllDevices,
    shouldIncludeAllEvents,
    shouldRepeat,
    shouldShowDays,
    shouldEnd,
    numberOfRepeats,
    days,
    selectedStartDate,
    selectedEndDate,
    selectedEndTime,
    timeZone,
    setShouldIncludeAllEvents,
    setShouldIncludeAllDevices,
    setSelectedStartDate,
    updateEndTime,
    updateDate,
    setSelectedEndDate,
    setSelectedEndTime,
    setShouldRepeat,
    setShouldShowDays,
    setShouldEnd,
    updateSelectedFacilityId,
    updateToggleAllFloors,
    updateSelectedFloorIds,
    updateNumberOfRepeats,
    updateSelectedDays,
    createRule,
    isActionOptionDisabled,
    facilityOptions,
    floorOptions,
    deviceOptions,
    emailOptions,
    isFacilitiesLoading,
    isFloorsLoading,
    isRecipientsLoading,
    isDevicesLoading,
    isLoading: isLoading || isAiOperatorRulesLoading,
    toggleAllDaySwitch: setIsAllDay,
    isAllDay,
  }
}
